/* ═══════════════════════════════════════════════════════════════════════════
   FF Design Carousel — JS
   Custom element that fetches sibling products sharing the same design: tag
   via Section Rendering API, injects product cards, marks the current
   product as "Currently Viewing", and manages frosted glass arrow navigation.
   Created: Feb 22 2026
   ═══════════════════════════════════════════════════════════════════════════ */

if (!customElements.get('ff-design-carousel')) {
  class FFDesignCarousel extends HTMLElement {

    connectedCallback() {
      this.designTag = this.dataset.designTag || '';
      this.currentHandle = this.dataset.currentHandle || '';
      this.viewingLabel = this.dataset.viewingLabel || 'Viewing';
      this.showViewingBadge = this.dataset.showViewingBadge !== 'false';
      this.viewingPosition = this.dataset.viewingPosition || 'center-image';
      this.hideRedundant = (this.dataset.arrowsHideRedundant || 'true') === 'true';
      this.limit = parseInt(this.dataset.limit, 10) || 50;
      this.fadeEnabled = this.dataset.fadeEnabled !== 'false';
      this.fadeWidth = parseFloat(this.dataset.fadeWidth) || 36;
      this.fadeOpacity = parseFloat(this.dataset.fadeOpacity);
      if (isNaN(this.fadeOpacity)) this.fadeOpacity = 0;

      if (!this.designTag) return;

      this.track = this.querySelector('.ff-dc__track');
      this.viewport = this.querySelector('.ff-dc__viewport');
      this.prevBtn = this.querySelector('.ff-dc__arrow--prev');
      this.nextBtn = this.querySelector('.ff-dc__arrow--next');

      if (!this.track) return;

      this._fetchDesignSiblings();
    }

    /* ── Fetch & render ──────────────────────────────────────────────── */

    async _fetchDesignSiblings() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}collections/all/${this.designTag}?section_id=ff-design-carousel-api`;

        const res = await fetch(url);
        if (!res.ok) return;

        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const cards = doc.querySelectorAll('.ff-dc__api-card');

        if (!cards.length) return;

        /* Apply limit */
        const limited = Array.from(cards).slice(0, this.limit);

        /* Filter: need at least 1 card that isn't the current product,
           OR keep all if we're showing current with "viewing" badge */
        const hasOthers = limited.some(
          (c) => c.dataset.handle !== this.currentHandle
        );
        if (!hasOthers) return; /* only current product in collection — collapse */

        /* ── Inject cards into track ──────────────────────────────── */
        this.track.innerHTML = '';

        limited.forEach((apiCard) => {
          const item = document.createElement('div');
          item.className = 'ff-dc__item';

          /* Mark current product */
          if (apiCard.dataset.handle === this.currentHandle) {
            item.classList.add('is-current');
            item.innerHTML = apiCard.innerHTML;

            /* Add "Viewing" badge */
            if (this.showViewingBadge) {
              const badge = document.createElement('div');
              badge.className = `ff-dc__viewing-badge ff-dc__viewing-badge--${this.viewingPosition}`;
              badge.textContent = this.viewingLabel;
              item.appendChild(badge);
            }
          } else {
            item.innerHTML = apiCard.innerHTML;
          }

          this.track.appendChild(item);
        });

        /* ── Reveal section ───────────────────────────────────────── */
        this.classList.add('ff-dc--loaded');

        /* Enable mask-image edge fades if setting is on */
        if (this.fadeEnabled) this.track.classList.add('has-fade');

        /* Bind secondary-image hover dwell on freshly injected cards */
        if (typeof window.FFInitHoverDwell === 'function') {
          window.FFInitHoverDwell(this);
        }

        /* ── Initialize arrows + scroll ───────────────────────────── */
        this._initArrows();
        this._initScrollState();

        /* Measure image area height for badge positioning */
        this._updateMediaHeight();

        /* Recompute on image loads */
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', () => {
              this._updateArrows();
              this._updateMediaHeight();
            }, { once: true });
          }
        });

        /* Observe late DOM injections (ratings widgets, prices, badges, etc.) */
        if ('MutationObserver' in window) {
          const mo = new MutationObserver(() => {
            if (this._moRaf) return;
            this._moRaf = requestAnimationFrame(() => {
              this._moRaf = null;
              this._updateArrows();
              this._updateMediaHeight();
            });
          });
          mo.observe(this.track, { childList: true, subtree: true });
        }

        /* Extra delayed syncs — widgets like Judge.me inject asynchronously */
        setTimeout(() => this._updateArrows(), 1200);

        /* ── Reinitialize Judge.me badges on injected cards ──────── */
        this._initJudgeMeBadges();

      } catch (e) {
        console.warn('ff-design-carousel: fetch failed', e);
      }
    }

    /* ── Arrow navigation ────────────────────────────────────────────── */

    _initArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._pageScroll('prev');
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._pageScroll('next');
        });
      }
    }

    _pageScroll(dir) {
      const page = Math.max(1, Math.floor(this.track.clientWidth * 0.9));
      this.track.scrollBy({
        left: dir === 'next' ? page : -page,
        behavior: 'smooth',
      });
    }

    /* ── Scroll state tracking (arrow visibility + edge fades) ────── */

    _initScrollState() {
      this._rafId = null;

      const throttledUpdate = () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._updateArrows();
          this._updateMediaHeight();
        });
      };

      this.track.addEventListener('scroll', throttledUpdate, { passive: true });

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(throttledUpdate);
        ro.observe(this.track);
      } else {
        window.addEventListener('resize', throttledUpdate);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(throttledUpdate).catch(() => {});
      }

      /* Initial + delayed syncs */
      this._updateArrows();
      requestAnimationFrame(() => this._updateArrows());
      setTimeout(() => this._updateArrows(), 120);
      setTimeout(() => this._updateArrows(), 400);
    }

    _updateArrows() {
      if (!this.track || !this.viewport) return;

      const overflow = this.track.scrollWidth > Math.ceil(this.track.clientWidth) + 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = Math.ceil(this.track.scrollLeft + this.track.clientWidth) >= this.track.scrollWidth - 1;

      this.viewport.classList.toggle('is-start', atStart || !overflow);
      this.viewport.classList.toggle('is-end', atEnd || !overflow);

      const setOff = (el, off) => {
        if (!el) return;
        el.classList.toggle('is-off', !!off);
        el.setAttribute('aria-hidden', off ? 'true' : 'false');
      };

      if (this.hideRedundant) {
        setOff(this.prevBtn, !overflow || atStart);
        setOff(this.nextBtn, !overflow || atEnd);
      } else {
        setOff(this.prevBtn, !overflow);
        setOff(this.nextBtn, !overflow);
      }

      /* Edge fade (mask-image driven by scroll position) */
      if (this.fadeEnabled) {
        if (!overflow) {
          this.track.style.removeProperty('--ffdc-fadeL');
          this.track.style.removeProperty('--ffdc-fadeR');
          this.track.style.removeProperty('--ffdc-fadeLa');
          this.track.style.removeProperty('--ffdc-fadeRa');
          return;
        }
        const maxX = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const x = Math.max(0, Math.min(this.track.scrollLeft, maxX));
        const lp = Math.min(x / this.fadeWidth, 1);
        const rp = Math.min((maxX - x) / this.fadeWidth, 1);
        const eL = 1 - (1 - lp) * (1 - lp);
        const eR = 1 - (1 - rp) * (1 - rp);
        this.track.style.setProperty('--ffdc-fadeL', (eL * this.fadeWidth) + 'px');
        this.track.style.setProperty('--ffdc-fadeR', (eR * this.fadeWidth) + 'px');
        this.track.style.setProperty('--ffdc-fadeLa', atStart ? '1' : String(this.fadeOpacity));
        this.track.style.setProperty('--ffdc-fadeRa', atEnd ? '1' : String(this.fadeOpacity));
      }
    }

    /* ── Reinitialize Judge.me badges on dynamically injected cards ── */
    _initJudgeMeBadges() {
      const tryInit = () => {
        if (window.jdgm && typeof jdgm.customizeBadges === 'function') {
          jdgm.customizeBadges();
          return true;
        }
        return false;
      };
      /* Try immediately, then retry a few times for slow script loads */
      if (!tryInit()) {
        let attempts = 0;
        const timer = setInterval(() => {
          if (tryInit() || ++attempts >= 10) clearInterval(timer);
        }, 500);
      }
    }

    /* ── Measure image area height for viewing badge positioning ──── */

    _updateMediaHeight() {
      const current = this.querySelector('.ff-dc__item.is-current');
      if (!current) return;

      const media = current.querySelector('.card__media');
      if (media) {
        current.style.setProperty('--ffdc-media-h', media.offsetHeight + 'px');
      }
    }
  }

  customElements.define('ff-design-carousel', FFDesignCarousel);
}